import {
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
  useClipboard,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {
  Banner,
  Link,
  LoadingOverlay,
  ModalConfirm,
  UserTooltip,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type SimpleTaskForClientFragment,
  type SimpleTaskFragment,
  type Task,
  UserScopeEnum,
  useCheckTaskRevisionLazyQuery,
  useDeleteTaskMutation,
  useGetPrioritiesOptionsQuery,
  useMarkTaskNotificationsAsReadMutation,
  useUpdateTaskPriorityMutation,
} from "@src/__generated__/graphql";
import { createTimeReportUrl } from "@src/components/modules/time-tracking/Report/time-report-url";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { BudgetIcon, Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { TaskPriorityBadge } from "@src/components/ui-kit/TaskPriorityBadge";
import { UncollapseOnHover } from "@src/components/ui-kit/UncollapseOnHover";
import { AddInternalUsersPopover } from "@src/components/widgets/Modals/ModalCommunication/AddInternalUsersPopover";
import { AddScopedUsersPopover } from "@src/components/widgets/Modals/ModalCommunication/AddScopedUsersPopover";
import { TaskTabs } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/TaskTabs";
import { PositionBadgeWithHours } from "@src/components/widgets/Modals/ModalCommunication/PositionBadgeWithHours";
import { SectionHeading } from "@src/components/widgets/Modals/ModalCommunication/SectionHeading";
import { ModalTaskEdit } from "@src/components/widgets/Modals/ModalTask/ModalTaskEdit";
import { trackEvent } from "@src/services/amplitude";
import { onError } from "@src/utils/apollo";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import mapToOptions from "@src/utils/map-to-options";
import { FormMode } from "@src/utils/types";
import { format } from "date-fns";
import { kebabCase } from "lodash";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, {
  Fragment,
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { TASK_ID_QUERY_KEY } from "./CommunicationModalHeader";
import { DrawerWrapper } from "./components/drawer-wrapper";
import { ModalWrapper } from "./components/modal-wrapper";

const COLLAPSED_POSITIONS_HEIGHT = 100;

export const TASK_REVISION_CHECK_INTERVAL = 10000;

export const timeToHrs = (time: number, decimals = 1): string => {
  const timeNum = typeof time === "number" ? time : Number(time ?? 0);
  return (timeNum / 3600).toFixed(decimals);
};

export function useHasTaskIdInUri(onHasTaskId: (taskId: Task["id"]) => void) {
  const { query } = useRouter();
  const taskId = query?.[TASK_ID_QUERY_KEY] as Task["id"] | undefined;

  useEffect(() => {
    if (!taskId) return;

    onHasTaskId(taskId);
  }, [taskId]);
}

export function useHasCommentIdInUri(onHasCommentId: () => void) {
  const { query } = useRouter();
  const taskId = query?.[TASK_ID_QUERY_KEY];
  const commentId = query?.commentId;

  useEffect(() => {
    if (!taskId) return;
    if (!commentId?.length) return;

    onHasCommentId();
  }, [taskId, commentId]);
}

export const ModalCommunication = observer(function ModalCommunication() {
  const {
    taskDetailModalStore: store,
    tasksListingStore,
    UIStore,
    workspaceStore,
  } = useStore();
  const { isMobile } = useScreenType();
  const budgetItem = store.task.value?.ourWorkBudgetItem;
  const discardModal = useDisclosure();
  const [showAllPositions, setShowAllPositions] = useState(false);
  const collapsePositionsRef = useRef<HTMLDivElement>(null);
  const [shouldCollapsePositions, setShouldCollapsePositions] = useState(false);
  const PositionsWrapper = shouldCollapsePositions ? Collapse : Fragment;
  const positionsProps = shouldCollapsePositions
    ? { in: showAllPositions, startingHeight: COLLAPSED_POSITIONS_HEIGHT }
    : {};
  const titleClipboard = useClipboard(store.task.value?.name ?? "");

  useEffect(() => {
    if (!titleClipboard.hasCopied) return;
    UIStore.toast({
      title: t`Task name copied to clipboard`,
      status: "success",
      isClosable: true,
    });
  }, [titleClipboard.hasCopied]);

  useLayoutEffect(() => {
    const rect = collapsePositionsRef.current?.getBoundingClientRect();
    if (rect) {
      setShouldCollapsePositions(rect.height >= COLLAPSED_POSITIONS_HEIGHT);
    }
  }, [store.isLoading.value, store.isLoadingDuplicate.value]);

  const { onCopy } = useClipboard(
    `${location.origin}/notifications?${TASK_ID_QUERY_KEY}=${store.task.value?.id}`,
  );

  const [checkTaskRevision] = useCheckTaskRevisionLazyQuery({
    variables: { id: store.taskId.value! },
    onCompleted(data) {
      if (!data.task.revision) return;
      if (!store.task.value?.revision) return;
      if (data.task.revision !== store.task.value.revision) {
        store.needsRevision.on();
      } else {
        store.needsRevision.off();
      }
    },
    ...onError(),
  });

  const [updatePriority, { loading: updatingPriority }] =
    useUpdateTaskPriorityMutation();

  useEffect(() => {
    if (store.taskId.value && store.disclosureState.isOpen) {
      markTaskNotificationsAsRead({
        variables: {
          task_id: store.taskId.value,
        },
      });
    }

    const id = setInterval(() => {
      if (
        store.taskId.value &&
        store.disclosureState.isOpen &&
        !store.descriptionLoading.value &&
        !store.editTaskModal.isOpen
      ) {
        checkTaskRevision();
      }
    }, TASK_REVISION_CHECK_INTERVAL);

    return () => clearInterval(id);
  }, [
    store.taskId.value,
    store.disclosureState.isOpen,
    store.editTaskModal.isOpen,
  ]);

  useGetPrioritiesOptionsQuery({
    skip: !store.disclosureState.isOpen,
    onCompleted(data) {
      if (data.taskPriorities) {
        store.priorityOptions = mapToOptions.priorities.toOptions(
          data.taskPriorities,
        );
      }
    },
  });

  const onUpdate = (task: SimpleTaskFragment | SimpleTaskForClientFragment) => {
    store.disclosureState.additionalData?.onChange?.(task);
    store.fetchTask();
  };

  const [markTaskNotificationsAsRead] = useMarkTaskNotificationsAsReadMutation({
    ...onError(),
  });

  const [deleteTask] = useDeleteTaskMutation({
    onCompleted() {
      UIStore.toast({
        title: t`The task has been deleted`,
        status: "success",
      });
      if (store.task.value) {
        store.disclosureState.additionalData?.onDelete?.(store.task.value);
        tasksListingStore.removeTask(
          store.task.value.id,
          store.task.value.parent?.id,
        );
      }
      store.disclosureState.onClose();
      trackEvent("task", "Deleted task");
    },
    ...onError(),
  });

  const handleCloseDisclosure = () => {
    if (store.descriptionChanged || store.commentChanged) {
      discardModal.onOpen();
    } else {
      store.disclosureState.onClose();
    }
  };

  return (
    <Fragment>
      <WrapComponent
        if={store.isDrawerMode}
        with={(children) => (
          <DrawerWrapper
            isOpen={store.disclosureState.isOpen}
            onClose={handleCloseDisclosure}
          >
            {children}
          </DrawerWrapper>
        )}
        elseWith={(children) => (
          <ModalWrapper
            isOpen={store.disclosureState.isOpen}
            onClose={handleCloseDisclosure}
          >
            {children}
          </ModalWrapper>
        )}
      >
        <LoadingOverlay
          isLoading={store.isLoading.value || store.isLoadingDuplicate.value}
        />

        {store.task.value && !store.isLoadingDuplicate.value && (
          <Box>
            {store.needsRevision.value && (
              <Banner
                status="warning"
                variant="contained"
                pl="8"
                pr="6"
                borderTop="4px"
                borderTopColor="orange.500"
                title={t`Someone has already edited the task`}
                description={t`Copy your changes to paste it after reloading the task.`}
                primaryAction={{
                  title: t`Reload task`,
                  loading: store.isLoading.value,
                  onClick: () => {
                    store.fetchTask();
                  },
                  leftIcon: <Icon name="repeat-04" />,
                }}
              />
            )}

            <Box justifyItems="start" w="full" pt="4">
              <Box w="full" px="8" pb="4">
                <HStack
                  alignItems="start"
                  justifyContent={isMobile ? "space-between" : undefined}
                  wrap={isMobile ? "wrap" : undefined}
                  mb="5"
                  fontWeight="medium"
                  spacing="4"
                >
                  <VStack
                    align="stretch"
                    flex="1"
                    flexBasis={isMobile ? "100%" : undefined}
                    spacing="3px"
                  >
                    <Heading
                      maxW={{
                        base: "300px",
                        "1.5xl": "350px",
                      }}
                      fontWeight="semibold"
                      size="md"
                    >
                      <UncollapseOnHover
                        hiddenComponent={
                          <Tooltip label={t`Copy link to task`}>
                            <Button
                              verticalAlign="top"
                              color="inherit"
                              fontSize="xs"
                              fontWeight="medium"
                              onClick={() => {
                                onCopy();
                                UIStore.toast({
                                  title: t`Link copied to clipboard`,
                                  status: "info",
                                  isClosable: true,
                                  duration: 3000,
                                });
                              }}
                              variant="link"
                            >
                              {store.task.value.id}
                            </Button>
                          </Tooltip>
                        }
                        dataComponentName="task-id-copy-link"
                        width="4"
                      >
                        <Tooltip label={store.task.value.name}>
                          <Text
                            fontSize="inherit"
                            fontWeight="inherit"
                            isTruncated
                            onClick={titleClipboard.onCopy}
                          >
                            {store.task.value.name}
                          </Text>
                        </Tooltip>
                      </UncollapseOnHover>
                    </Heading>
                    <SectionHeading>
                      <Trans>Project:</Trans>{" "}
                      {store.task.value.ourWorkBudgetItem ? (
                        <Tooltip
                          label={`${store.task.value.ourWorkBudgetItem.project.brand.client.name} - ${store.task.value.ourWorkBudgetItem.project.brand.name} - ${store.task.value.ourWorkBudgetItem.project.title}`}
                        >
                          <span>
                            <Link
                              target="_blank"
                              href={{
                                pathname: "/projects/detail",
                                query: {
                                  id: store.task.value.ourWorkBudgetItem.project
                                    .id,
                                },
                              }}
                              color="inherit"
                            >
                              {`${store.task.value.ourWorkBudgetItem.project.code} / ${store.task.value.ourWorkBudgetItem.project.title}`}
                            </Link>
                          </span>
                        </Tooltip>
                      ) : (
                        <Trans>Unassigned</Trans>
                      )}
                    </SectionHeading>
                    {store.isInternalUser && (
                      <SectionHeading>
                        <Trans>Budget item:</Trans>{" "}
                        {store.task.value.ourWorkBudgetItem &&
                        "title" in store.task.value.ourWorkBudgetItem ? (
                          <Tooltip
                            label={`${store.task.value.ourWorkBudgetItem.project.brand.client.name} - ${store.task.value.ourWorkBudgetItem.project.brand.name} - ${store.task.value.ourWorkBudgetItem.project.title}`}
                          >
                            <span>
                              <Link
                                href={{
                                  pathname: "/projects/detail",
                                  query: {
                                    id: store.task.value.ourWorkBudgetItem
                                      .project.id,
                                  },
                                  hash: kebabCase(
                                    store.task.value.ourWorkBudgetItem.title,
                                  ),
                                }}
                                color="inherit"
                                target="_blank"
                              >
                                {store.task.value.ourWorkBudgetItem.title}
                              </Link>
                            </span>
                          </Tooltip>
                        ) : (
                          <Trans>Unassigned</Trans>
                        )}
                      </SectionHeading>
                    )}
                    {store.task.value.createdBy && (
                      <SectionHeading>
                        <HStack spacing="1">
                          <Trans>Creator:</Trans>
                          <UserTooltip user={store.task.value.createdBy}>
                            {store.task.value.createdBy.full_name}
                          </UserTooltip>
                        </HStack>
                      </SectionHeading>
                    )}
                  </VStack>
                  <Grid
                    gap="4"
                    templateColumns={`repeat(${store.taskInfoColumnCount}, 1fr)`}
                  >
                    <GridItem>
                      <TaskHeaderInfo
                        title={t`Priority`}
                        icon={
                          <Icon
                            name="bar-chart-03"
                            transform="rotate(90deg)"
                            boxSize="5"
                          />
                        }
                      >
                        <TaskPriorityBadge
                          rounded="base"
                          priority={store.task.value.priority}
                          isEditable
                          taskPriorities={store.taskPriorities.value}
                          isLoading={updatingPriority}
                          onChange={async (newPriorityId) => {
                            if (
                              !store.task.value?.id ||
                              typeof newPriorityId !== "string"
                            )
                              return;
                            updatePriority({
                              variables: {
                                task_id: store.task.value.id,
                                priority_id: newPriorityId,
                              },
                            }).then(({ data }) => {
                              if (!data || !store.task.value) return;
                              store.task.value.priority =
                                data.updateTaskPriority.priority;
                            });
                          }}
                        />
                      </TaskHeaderInfo>
                    </GridItem>

                    {store.showTaskInfo.tracked && (
                      <GridItem>
                        <TaskHeaderInfo
                          title={t`Tracked`}
                          icon={<TrackedTimeIcon boxSize="5" />}
                        >
                          {budgetItem && (
                            <Link
                              href={createTimeReportUrl({
                                projectId: budgetItem.project.id,
                                budgetItemId: budgetItem.id,
                                taskId: store.task.value?.id,
                                parentTaskId: store.task.value.parent?.id,
                              })}
                              onClick={() => store.disclosureState.onClose()}
                            >
                              <Text fontWeight="medium" size="xs">
                                {timeToHrs(
                                  store.task.value.stats?.spent_time ?? 0,
                                )}
                              </Text>
                            </Link>
                          )}
                        </TaskHeaderInfo>
                      </GridItem>
                    )}

                    {store.showTaskInfo.budget && (
                      <GridItem>
                        <TaskHeaderInfo
                          title={t`Budget`}
                          icon={<BudgetIcon boxSize="5" />}
                        >
                          {budgetItem && (
                            <Link
                              href={{
                                pathname:
                                  "uses_budgetovac" in budgetItem.project &&
                                  budgetItem.project?.uses_budgetovac
                                    ? "/projects/budgets"
                                    : "/projects/budget",
                                query: { id: budgetItem.project.id },
                              }}
                              onClick={() => store.disclosureState.onClose()}
                            >
                              <Text fontWeight="medium" size="xs">
                                {timeToHrs(
                                  store.task.value.stats?.planned_time ?? 0,
                                )}
                              </Text>
                            </Link>
                          )}
                        </TaskHeaderInfo>
                      </GridItem>
                    )}

                    {store.addTaskInfoSpacer && (
                      <GridItem colSpan={store.taskInfoSpacerSpan} />
                    )}

                    {store.showTaskInfo.duration && (
                      <GridItem>
                        <TaskHeaderInfo
                          title={t`Duration`}
                          icon={<Icon name="clock-fast-forward" boxSize="5" />}
                        >
                          {store.task.value.from && (
                            <Text color="black" fontWeight="medium" size="xs">
                              {format(store.task.value.from, "MMM d, yyyy")}
                            </Text>
                          )}
                          {store.task.value.to && (
                            <Text color="black" fontWeight="medium" size="xs">
                              {format(store.task.value.to, "MMM d, yyyy")}
                            </Text>
                          )}
                        </TaskHeaderInfo>
                      </GridItem>
                    )}

                    {store.showTaskInfo.deadline && (
                      <GridItem>
                        <TaskHeaderInfo
                          title={t`Deadline`}
                          icon={<Icon name="calendar-date" boxSize="5" />}
                        >
                          <Text color="black" fontWeight="medium" size="xs">
                            {format(
                              store.task.value.deadlineDate ?? new Date(),
                              "MMM d, yyyy",
                            )}
                          </Text>
                        </TaskHeaderInfo>
                      </GridItem>
                    )}
                  </Grid>
                </HStack>

                <Stack role="group">
                  <HStack spacing="2">
                    <SectionHeading iconName="users-01">
                      <Trans>Assigned users</Trans>
                    </SectionHeading>

                    {store.isInternalUser && (
                      <Menu>
                        <MenuButton>
                          <IconButton
                            borderRadius="full"
                            aria-label={t`Assign new user`}
                            colorScheme="grey"
                            icon={<Icon name="plus" />}
                            size="xs"
                            variant="outline"
                          />
                        </MenuButton>

                        <MenuList zIndex="11">
                          <MenuItem
                            onClick={store.addInternalUserDisclosure.onToggle}
                          >
                            <Trans>Add user</Trans>
                          </MenuItem>
                          <MenuItem
                            onClick={store.addClientDisclosure.onToggle}
                          >
                            <Trans>Add client</Trans>
                          </MenuItem>
                          {workspaceStore.settings?.partnerAccessEnabled && (
                            <MenuItem
                              onClick={store.addPartnerDisclosure.onToggle}
                            >
                              <Trans>Add partner</Trans>
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    )}

                    <AddScopedUsersPopover
                      scope={UserScopeEnum.Client}
                      userIds={store.clientIds}
                      usersIdsFormOtherScope={store.partnerIds}
                      disclosure={store.addClientDisclosure}
                    >
                      <Box />
                    </AddScopedUsersPopover>
                    {workspaceStore.settings?.partnerAccessEnabled && (
                      <AddScopedUsersPopover
                        scope={UserScopeEnum.Partner}
                        userIds={store.partnerIds}
                        disclosure={store.addPartnerDisclosure}
                        usersIdsFormOtherScope={store.clientIds}
                      >
                        <Box />
                      </AddScopedUsersPopover>
                    )}
                    <AddInternalUsersPopover
                      disclosure={store.addInternalUserDisclosure}
                    />
                  </HStack>

                  <Box>
                    <Box mt="2" mb="4">
                      <Flex ref={collapsePositionsRef} direction="column">
                        <PositionsWrapper {...positionsProps}>
                          <Flex wrap="wrap">
                            {store.task.value.assignedUsers.map((position) => (
                              <PositionBadgeWithHours
                                key={`${position.user?.id}-${position.timeTrackingWorkType?.title}`}
                                mr="2"
                                mb="2"
                                workTypeTitle={
                                  position.timeTrackingWorkType?.title
                                }
                                user={position.user}
                                reportedValue={position.spent_time}
                                plannedValue={position.planned_time}
                              />
                            ))}
                          </Flex>
                        </PositionsWrapper>
                        <Flex
                          pos="relative"
                          direction="column"
                          hidden={!shouldCollapsePositions}
                        >
                          <Box
                            pos="absolute"
                            top="-20px"
                            w="100%"
                            h="20px"
                            bg={
                              showAllPositions
                                ? ""
                                : "linear-gradient(180deg, transparent 0%, #fff 100%)"
                            }
                            pointerEvents="none"
                          />
                          <IconButton
                            aria-label={t`Show all positions`}
                            icon={
                              <Icon
                                name={
                                  showAllPositions
                                    ? "chevron-up"
                                    : "chevron-down"
                                }
                              />
                            }
                            onClick={() => {
                              setShowAllPositions(!showAllPositions);
                            }}
                            variant="ghost"
                          />
                        </Flex>
                      </Flex>
                      {!!store.task.value.otherPeopleTrackedTotal &&
                        store.task.value.otherPeopleTracked?.length && (
                          <Tooltip
                            label={store.task.value.otherPeopleTracked?.map(
                              ({
                                user,
                                tracked_time,
                                time_tracking_work_type_id,
                              }) => {
                                const foundPosition =
                                  store.task.value?.ourWorkBudgetItem?.project.availableTimeTrackingWorkTypes.find(
                                    ({ id }) =>
                                      id === time_tracking_work_type_id,
                                  );

                                return (
                                  <Text
                                    key={
                                      user.full_name +
                                      time_tracking_work_type_id
                                    }
                                  >
                                    {user.full_name +
                                      " / " +
                                      foundPosition?.title +
                                      ": " +
                                      timeToHrs(tracked_time) +
                                      "h"}
                                  </Text>
                                );
                              },
                            )}
                          >
                            <Text w="fit-content" color="grey.600">
                              <Trans>Tracked by unassigned users:</Trans>
                              &nbsp;
                              <Text display="inline" color="grey.900">
                                {timeToHrs(
                                  store.task.value.otherPeopleTrackedTotal,
                                )}
                              </Text>
                            </Text>
                          </Tooltip>
                        )}
                    </Box>
                    {!!store.task.value.cloudUrl && (
                      <SharepointLink
                        mt="6"
                        href={store.task.value.cloudUrl ?? ""}
                      />
                    )}
                    {!!store.task.value.driveCloudUrl && (
                      <GoogleDriveLink
                        mt="6"
                        href={store.task.value.driveCloudUrl ?? ""}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>

              {/* Workaround - to rerender editors initial value after edit from modal  */}
              {!store.isLoading.value && <TaskTabs />}
            </Box>
          </Box>
        )}
      </WrapComponent>

      {store.task.value && (
        <React.Fragment>
          <ModalConfirm
            destructive
            cancelBtnTitle={t`Cancel`}
            confirmBtnTitle={t`Delete`}
            onConfirm={() => {
              if (store.task.value) {
                deleteTask({ variables: { id: store.task.value.id } });
              }
            }}
            isOpen={store.deleteModal.isOpen}
            onClose={store.deleteModal.onClose}
          >
            <Box>
              {store.task.value.cloudUrl && (
                <Banner
                  title={t`When you delete the task, the corresponding folder on SharePoint will be automatically removed.`}
                  status="warning"
                  mb="4"
                />
              )}
              <Text>
                <Trans>Are you sure that you want to delete task</Trans>{" "}
                {store.task.value.name}?
              </Text>
            </Box>
          </ModalConfirm>
          <ModalTaskEdit
            taskId={store.task.value.id}
            projectId={store.task.value.ourWorkBudgetItem?.project.id}
            onUpdate={onUpdate}
            isOpen={store.editTaskModal.isOpen}
            onClose={() => {
              store.editTaskModal.onClose();

              runInAction(() => {
                store.editTaskModal.additionalData = undefined;
              });
            }}
            priorityOptions={store.priorityOptions}
            statusOptions={store.statusOptions}
            filterParentTasksForGroup={Boolean(
              store.task.value.task_template_group_id,
            )}
            mode={
              store.editTaskModal.additionalData?.moveMode
                ? FormMode.MOVE
                : FormMode.EDIT
            }
          />
        </React.Fragment>
      )}
      <ModalConfirm
        isOpen={discardModal.isOpen}
        onClose={discardModal.onClose}
        onConfirm={store.disclosureState.onClose}
        confirmBtnTitle={t`Discard changes`}
        destructive
      >
        <Trans>You have unsaved</Trans>
        &nbsp;
        {store.descriptionChanged && <Trans>description</Trans>}
        {store.descriptionChanged && store.commentChanged && (
          <>
            &nbsp;<Trans>and</Trans>&nbsp;
          </>
        )}
        {store.commentChanged && <Trans>comment</Trans>}
        &nbsp;
        <Trans>changes. Would you like to discard them?</Trans>
      </ModalConfirm>
    </Fragment>
  );
});

type TaskHeaderInfoProps = {
  title: string;
  icon: ReactElement;
};
const TaskHeaderInfo = observer(function TaskHeaderInfo({
  title,
  icon,
  children,
}: PropsWithChildren<TaskHeaderInfoProps>) {
  return (
    <HStack align="start" spacing="3">
      <Center boxSize="10" bg="gray.200" rounded="lg">
        {icon}
      </Center>
      <VStack align="start" spacing="0">
        <Text color="grey" fontSize="xs">
          {title}
        </Text>
        {children}
      </VStack>
    </HStack>
  );
});
